"use client";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { formatQueryCount } from "@/utils/api-pricing";
import { Check, Loader2 } from "lucide-react";
import React from "react";
import { createContext, useContext, useState } from "react";

export interface Product {
	id: string;
	name: string;
	description?: string;
	everythingFrom?: string;

	buttonText?: string;
	buttonUrl?: string;

	recommendedText?: string;

	/** Monthly API query quota when billing.api_pricing is enabled. */
	apiQueriesIncluded?: number;

	price: {
		primaryText: string;
		secondaryText?: string;
	};

	priceAnnual?: {
		primaryText: string;
		secondaryText?: string;
	};

	items: {
		primaryText: string;
		secondaryText?: string;
	}[];
}

const PricingTableContext = createContext<{
	products: Product[];
	showFeatures: boolean;
	isAnnual: boolean;
	setIsAnnual: (isAnnual: boolean) => void;
}>({
	products: [],
	showFeatures: true,
	isAnnual: false,
	setIsAnnual: () => { },
});

export const usePricingTableContext = (componentName: string) => {
	const context = useContext(PricingTableContext);

	if (context === undefined) {
		throw new Error(`${componentName} must be used within <PricingTable />`);
	}

	return context;
};

type PricingTableProps = {
	children?: React.ReactNode;
	products?: Product[];
	showFeatures?: boolean;
	className?: string;
	isAnnual: boolean;
	setIsAnnual: (isAnnual: boolean) => void;
}

export const PricingTable = ({
	children,
	products,
	showFeatures = true,
	className,
	isAnnual,
	setIsAnnual,
}: PricingTableProps) => {
	if (!products) {
		throw new Error("products is required in <PricingTable />");
	}

	if (products.length === 0) {
		return <></>;
	}
	const hasEvenProducts = products.length % 2 === 0;

	return (
		<PricingTableContext.Provider
			value={{ isAnnual, setIsAnnual, products, showFeatures }}
		>
			<div className={cn("flex items-center flex-col w-full")}>
				{products.some((p) => p.priceAnnual) && (
					<div
						className={cn(products.some((p) => p.recommendedText) && "mb-10")}
					>
						<AnnualSwitch isAnnual={isAnnual} setIsAnnual={setIsAnnual} />
					</div>
				)}
				<div
					className={cn(
						"w-full grid grid-cols-1 lg:grid-cols-none lg:auto-cols-[minmax(280px,1fr)] lg:grid-flow-col gap-4 lg:gap-6",
						hasEvenProducts && "sm:grid-cols-2",
						className,
					)}
				>
					{children}
				</div>
			</div>
		</PricingTableContext.Provider>
	);
};

interface PricingCardProps {
	productId: string;
	showFeatures?: boolean;
	className?: string;
	onButtonClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	buttonProps?: React.ComponentProps<"button">;
	showBuyButton?: boolean;
	isSelected?: boolean;
	onSelect?: () => void;
}

export const PricingCard = ({
	productId,
	className,
	onButtonClick,
	buttonProps,
	showBuyButton = true,
	isSelected = false,
	onSelect,
}: PricingCardProps) => {
	const { isAnnual, products, showFeatures } =
		usePricingTableContext("PricingCard");

	const product = products.find((p) => p.id === productId);

	if (!product) {
		throw new Error(`Product with id ${productId} not found`);
	}

	const {
		name,
		price,
		priceAnnual,
		recommendedText,
		buttonText,
		items,
		description,
		buttonUrl,
		everythingFrom,
		apiQueriesIncluded,
	} = product;

	const isRecommended = !!recommendedText;
	const isSelectable = !!onSelect;
	// In selection mode, only the chosen card is emphasized — not the static "Best value" tier.
	const emphasizeRecommended = isRecommended && !isSelectable;
	const emphasizeSelected = isSelectable && isSelected;

	const handleCardSelect = () => {
		onSelect?.();
	};

	return (
		<div
			role={isSelectable ? "button" : undefined}
			tabIndex={isSelectable ? 0 : undefined}
			aria-pressed={isSelectable ? isSelected : undefined}
			onClick={isSelectable ? handleCardSelect : undefined}
			onKeyDown={
				isSelectable
					? (e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								handleCardSelect();
							}
						}
					: undefined
			}
			className={cn(
				"w-full h-full relative rounded-2xl border transition-all duration-300",
				"bg-card/50 backdrop-blur-sm",
				"hover:shadow-lg hover:shadow-primary/5 dark:hover:shadow-primary/10",
				"hover:border-primary/20 dark:hover:border-primary/30",
				emphasizeRecommended &&
					"border-primary/30 dark:border-primary/40 shadow-xl shadow-primary/10 dark:shadow-primary/20 bg-gradient-to-br from-card to-card/80 dark:from-card dark:to-card/90 ring-2 ring-primary/10 dark:ring-primary/20 lg:scale-105 lg:z-10",
				!emphasizeRecommended &&
					!emphasizeSelected &&
					"border-border/50 dark:border-border/30",
				emphasizeSelected &&
					"border-primary ring-2 ring-primary/40 shadow-lg shadow-primary/15 lg:scale-[1.02] lg:z-10",
				isSelectable && "cursor-pointer",
				className,
			)}
		>
			{recommendedText && <RecommendedBadge recommended={recommendedText} />}
			<div className="flex flex-col h-full p-6 lg:p-8">
				<div className="h-full flex flex-col">
					<div className="flex flex-col gap-3 mb-6">
						<h2 className="text-2xl lg:text-3xl font-bold tracking-tight">
							{name}
						</h2>
						{description && (
							<p className="text-sm text-muted-foreground leading-relaxed">
								{description}
							</p>
						)}
						<div className="mt-4 mb-2">
							<div className="flex items-baseline gap-2">
								<span className="text-4xl lg:text-5xl font-bold tracking-tight">
									{isAnnual && priceAnnual
										? priceAnnual?.primaryText
										: price.primaryText}
								</span>
								{price.secondaryText && (
									<span className="text-base font-normal text-muted-foreground">
										{isAnnual && priceAnnual
											? priceAnnual?.secondaryText
											: price.secondaryText}
									</span>
								)}
							</div>
							{apiQueriesIncluded !== undefined && (
								<p className="mt-2 text-sm text-muted-foreground">
									<span className="font-medium text-foreground">
										{formatQueryCount(apiQueriesIncluded)}
									</span>{" "}
									API queries / month included
								</p>
							)}
						</div>
					</div>
					{showFeatures && items.length > 0 && (
						<div className="flex-grow mb-8">
							<PricingFeatureList
								items={items}
								showIcon={true}
								everythingFrom={everythingFrom}
							/>
						</div>
					)}
				</div>
				<div className={cn("mt-auto")}>
					{showBuyButton && (
						<PricingCardButton
							recommended={isRecommended || isSelected}
							onClick={(e) => {
								e.stopPropagation();
								onButtonClick?.(e);
							}}
							buttonUrl={buttonUrl}
							{...buttonProps}
						>
							{buttonProps?.children || buttonText}
						</PricingCardButton>
					)}
				</div>
			</div>
		</div>
	);
};

// Pricing Feature List
export const PricingFeatureList = ({
	items,
	showIcon = true,
	everythingFrom,
	className,
}: {
	items: {
		primaryText: string;
		secondaryText?: string;
	}[];
	showIcon?: boolean;
	everythingFrom?: string;
	className?: string;
}) => {
	return (
		<div className={cn("flex-grow", className)}>
			{everythingFrom && (
				<p className="text-sm mb-6 text-muted-foreground">
					Everything from {everythingFrom}, plus:
				</p>
			)}
			<div className="space-y-4">
				{items.map((item, index) => (
					<div
						key={index}
						className="flex items-start gap-3 text-sm group/item"
					>
						{showIcon && (
							<div className="flex-shrink-0 mt-0.5">
								<div className="rounded-full bg-primary/10 dark:bg-primary/20 p-1 group-hover/item:bg-primary/20 dark:group-hover/item:bg-primary/30 transition-colors">
									<Check className="h-3.5 w-3.5 text-primary" />
								</div>
							</div>
						)}
						<div className="flex flex-col pt-0.5">
							<span className="leading-relaxed">{item.primaryText}</span>
							{item.secondaryText && (
								<span className="text-xs text-muted-foreground mt-1">
									{item.secondaryText}
								</span>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Pricing Card Button
export interface PricingCardButtonProps extends React.ComponentProps<"button"> {
	recommended?: boolean;
	buttonUrl?: string;
}

export const PricingCardButton = React.forwardRef<
	HTMLButtonElement,
	PricingCardButtonProps
>(({ recommended, children, buttonUrl, onClick, className, ...props }, ref) => {
	const [loading, setLoading] = useState(false);
	return (
		<Button
			className={cn(
				"w-full py-6 px-6 group overflow-hidden relative transition-all duration-300",
				"font-semibold text-base",
				recommended
					? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg shadow-primary/20 dark:shadow-primary/30"
					: "bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border/50 dark:border-border/30 hover:border-primary/30 dark:hover:border-primary/40",
				"rounded-xl",
				className,
			)}
			{...(ref ? { ref } : {})}
			disabled={loading}
			onClick={async (e) => {
				setLoading(true);
				try {
					if (onClick) {
						await onClick(e);

						return;
					}

					if (buttonUrl) {
						window.open(buttonUrl, "_blank");
						return;
					}
				} catch (error) {
					throw error;
				} finally {
					setLoading(false);
				}
			}}
			{...props}
		>
			{loading ? (
				<Loader2 className="h-5 w-5 animate-spin" />
			) : (
				<>
					<div className="flex items-center justify-center gap-2 w-full transition-transform duration-300 group-hover:translate-y-[-130%]">
						<span>{children}</span>
						<span className="text-lg transition-transform group-hover:translate-x-1">
							→
						</span>
					</div>
					<div className="flex items-center justify-center gap-2 w-full absolute px-6 translate-y-[130%] transition-transform duration-300 group-hover:translate-y-0">
						<span>{children}</span>
						<span className="text-lg transition-transform group-hover:translate-x-1">
							→
						</span>
					</div>
				</>
			)}
		</Button>
	);
});
PricingCardButton.displayName = "PricingCardButton";

// Annual Switch
export const AnnualSwitch = ({
	isAnnual,
	setIsAnnual,
}: {
	isAnnual: boolean;
	setIsAnnual: (isAnnual: boolean) => void;
}) => {
	return (
		<div className="flex items-center gap-4 mb-6 h-12">
			<span
				className={cn(
					"text-sm font-medium transition-colors duration-200",
					!isAnnual
						? "text-foreground"
						: "text-muted-foreground",
				)}
			>
				Monthly
			</span>
			<Switch
				id="annual-billing"
				checked={isAnnual}
				onCheckedChange={setIsAnnual}
				className="data-[state=checked]:bg-primary"
			/>
			<span
				className={cn(
					"text-sm font-medium transition-colors duration-200",
					isAnnual
						? "text-foreground"
						: "text-muted-foreground",
				)}
			>
				Annual
			</span>
			<span
				className={cn(
					"text-xs font-medium text-primary bg-primary/10 dark:bg-primary/20 px-2 py-1 rounded-full transition-opacity duration-200 whitespace-nowrap",
					isAnnual ? "opacity-100" : "opacity-0 pointer-events-none",
				)}
			>
				Save 20%
			</span>
		</div>
	);
};

export const RecommendedBadge = ({ recommended }: { recommended: string }) => {
	return (
		<div className="absolute top-4 right-4 z-10">
			<div className="relative">
				<div className="absolute inset-0 bg-primary/20 dark:bg-primary/30 blur-xl rounded-full" />
				<div className="relative bg-gradient-to-r from-primary to-primary/80 dark:from-primary dark:to-primary/90 text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg shadow-primary/20 dark:shadow-primary/30">
					{recommended}
				</div>
			</div>
		</div>
	);
};
